import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

// ETOLIE 04.09.2026: podgląd diagnostyczny filmu w galerii. Powstaje WYŁĄCZNIE
// wtedy, gdy adres strony ma ?etolie-debug-wideo=1 — parametr czytamy raz, przy
// ładowaniu modułu. Bez niego nie ma ani nakładki w DOM, ani nasłuchów, ani
// jednego wpisu w konsoli. Potrzebny, bo zachowania WebKita na iPhonie nie da
// się odtworzyć z naszego środowiska: właściciel otwiera PDP z parametrem,
// przewija do filmu i wysyła zrzut ekranu z ciągiem zdarzeń.
const videoDebugEnabled = (() => {
  try {
    return new URLSearchParams(window.location.search).get('etolie-debug-wideo') === '1';
  } catch {
    return false;
  }
})();

/**
 * ETOLIE 04.09.2026: mała, nieinteraktywna ramka na dole ekranu z osią czasu
 * zdarzeń każdego filmu i odświeżanym co sekundę stanem odtwarzacza.
 * @returns {{ log: (element: Element, type: string, data?: Record<string, unknown>) => void }}
 */
function createVideoDebugOverlay() {
  const maxLines = 24;
  const startedAt = performance.now();
  /** @type {WeakMap<Element, string>} */
  const labels = new WeakMap();
  /** @type {Set<Element>} */
  const watched = new Set();
  /** @type {string[]} */
  const lines = [];
  /** @type {HTMLElement | null} */
  let box = null;
  /** @type {HTMLElement | null} */
  let head = null;
  /** @type {HTMLElement | null} */
  let body = null;
  let counter = 0;

  const mount = () => {
    if (box || !document.body) return;
    box = document.createElement('div');
    box.style.cssText =
      'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;pointer-events:none;' +
      'box-sizing:border-box;max-height:45vh;overflow:hidden;padding:6px 8px;' +
      'display:flex;flex-direction:column;justify-content:flex-end;' +
      'background:rgba(0,0,0,0.82);color:#b8f7a4;text-align:left;' +
      'font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace;white-space:pre-wrap;word-break:break-word';
    head = document.createElement('div');
    head.style.cssText = 'color:#8fdcff;margin-bottom:3px';
    head.textContent = 'etolie-debug-wideo';
    body = document.createElement('div');
    box.appendChild(head);
    box.appendChild(body);
    document.body.appendChild(box);
  };

  /** @param {Element} element */
  const labelFor = (element) => {
    const known = labels.get(element);
    if (known) return known;
    counter += 1;
    const name = `V${counter}${element.closest('dialog') ? '~zoom' : ''}`;
    labels.set(element, name);
    watched.add(element);
    return name;
  };

  const refresh = () => {
    mount();
    if (!head) return;
    const rows = [];
    for (const element of watched) {
      const video = element.querySelector('video');
      if (!video) {
        rows.push(`${labels.get(element)} brak <video>`);
        continue;
      }
      const buffered = video.buffered.length ? video.buffered.end(video.buffered.length - 1) : 0;
      rows.push(
        `${labels.get(element)} rs=${video.readyState} ns=${video.networkState}` +
          ` paused=${video.paused ? 1 : 0} t=${video.currentTime.toFixed(1)} buf=${buffered.toFixed(1)}`
      );
    }
    head.textContent = `etolie-debug-wideo ${new Date().toLocaleTimeString('pl-PL')}\n${
      rows.join('\n') || 'brak odtwarzaczy'
    }`;
  };

  setInterval(refresh, 1000);

  return {
    log(element, type, data) {
      const parts = [];
      for (const [key, value] of Object.entries(data ?? {})) {
        if (value === undefined || value === null) continue;
        parts.push(`${key}=${value}`);
      }
      const stamp = String(Math.round(performance.now() - startedAt)).padStart(5, '0');
      lines.push(`+${stamp} ${labelFor(element)} ${type}${parts.length ? ` ${parts.join(' ')}` : ''}`);
      if (lines.length > maxLines) lines.splice(0, lines.length - maxLines);
      mount();
      if (body) body.textContent = lines.join('\n');
      refresh();
    },
  };
}

const videoDebug = videoDebugEnabled ? createVideoDebugOverlay() : null;

/** ETOLIE 04.09.2026: zdarzenia `<video>` wypisywane tylko w trybie diagnostycznym. */
const debuggedVideoEvents = [
  'loadstart',
  'loadedmetadata',
  'loadeddata',
  'canplay',
  'playing',
  'pause',
  'waiting',
  'stalled',
  'suspend',
  'error',
  'ended',
];

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #autoplayObserver;

  /**
   * Incremented by every explicit pause. A rejected play() from an older
   * generation can then be identified as the expected play/pause race without
   * hiding unrelated playback failures.
   * @type {number}
   */
  #playGeneration = 0;

  /**
   * ETOLIE 04.09.2026: slajd z tym filmem jest bieżący albo stoi w kadrze.
   * @type {boolean}
   */
  #wantsPlayback = false;

  /**
   * ETOLIE 04.09.2026: film choć raz naprawdę ruszył (zdarzenie `playing`).
   * Dopiero wtedy wolno go pauzować.
   * @type {boolean}
   */
  #hasPlayed = false;

  /**
   * ETOLIE 04.09.2026: `<video>`, do którego podpięte są nasłuchy startu.
   * @type {HTMLVideoElement | null}
   */
  #armedVideo = null;

  /**
   * ETOLIE 04.09.2026: obietnica play() czeka na rozstrzygnięcie. Pilnuje, żeby
   * w danej chwili leciało dokładnie jedno żądanie odtwarzania.
   * @type {boolean}
   */
  #playPending = false;

  /**
   * ETOLIE 04.09.2026: film wysłał `loadstart`, czyli algorytm ładowania ruszył.
   * @type {boolean}
   */
  #loadStarted = false;

  /**
   * ETOLIE 04.09.2026: odczekaliśmy już jedno zadanie na `loadstart`.
   * @type {boolean}
   */
  #autostartWaited = false;

  /**
   * ETOLIE 04.09.2026: odroczenie autostartu stoi w kolejce.
   * @type {boolean}
   */
  #autostartDeferred = false;

  /**
   * ETOLIE 04.09.2026: jedno ponowienie po AbortError zostało zużyte.
   * @type {boolean}
   */
  #autostartRetried = false;

  /**
   * ETOLIE 04.09.2026: slajd BYŁ bieżący i przestał nim być. Odróżnia „klientka
   * zeszła ze slajdu" od zwykłego „jeszcze nie dojechał".
   * @type {boolean}
   */
  #released = false;

  #handleReducedMotionChange = () => {
    if (!this.hasAttribute('autoplay')) return;
    if (this.autoplayAllowed()) {
      this.#observeForAutoplay();
      return;
    }

    this.#stopAutoplayObserver();
    if (this.hasAttribute('data-media-loaded')) this.pauseMedia();
  };

  connectedCallback() {
    super.connectedCallback();
    // Sekcje Shopify mogą odłączyć i ponownie podłączyć ten sam element.
    // AbortController po abort() nie nadaje się do ponownego użycia.
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(
      ThemeEvents.mediaStartedPlaying,
      (event) => {
        // ETOLIE 04.09.2026: własne zdarzenie nie może pauzować własnego filmu.
        if (event instanceof MediaStartedPlayingEvent && event.detail?.resource === this) return;
        this.pauseMedia();
      },
      { signal }
    );
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    reducedMotionQuery.addEventListener('change', this.#handleReducedMotionChange);
    this.#observeForAutoplay();
  }

  /**
   * Whether this element may start without an explicit customer action.
   * Manual play remains available from the poster/toggle under reduced motion.
   * @returns {boolean}
   */
  autoplayAllowed() {
    return this.hasAttribute('autoplay') && !reducedMotionQuery.matches;
  }

  #observeForAutoplay() {
    if (!this.autoplayAllowed() || this.#autoplayObserver) return;

    // ETOLIE: autoplay media is created only when it is actually visible. This
    // avoids downloading off-screen product videos and prevents video.liquid's
    // template from coexisting with a second, eagerly-rendered player.
    this.#autoplayObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#debug('io', { ratio: entry.intersectionRatio.toFixed(2), vis: entry.isIntersecting ? 1 : 0 });
          // ETOLIE 04.09.2026: próg 0.5 nigdy nie był bramką startu — obserwator
          // kolejkuje wpis także przy samej zmianie isIntersecting (ratio ~0),
          // więc start leciał na slajd ledwie wysunięty zza krawędzi, w trakcie
          // przewijania karuzeli. Start bierzemy z ratio; warunek pauzy zostaje
          // dokładnie ten sam co dotąd: całkowite zejście z kadru.
          // ETOLIE 19.09.2026 (Jarek): próg zszedł z 0.5 na 0.25. Na desktopie kafelek filmu jest wysoki,
          // więc przy połowie klientka widziała trójkąt i czekała, aż dojedzie do środka kafelka.
          if (entry.intersectionRatio >= 0.25) {
            if (!this.autoplayAllowed()) continue;
            this.#setWantsPlayback(true);
            this.loadContent(false);
            this.#tryAutostart();
          } else if (!entry.isIntersecting) {
            this.#setWantsPlayback(false);
            if (this.hasAttribute('data-media-loaded')) this.pauseMedia();
          }
        }
      },
      { threshold: [0, 0.25] }
    );
    this.#autoplayObserver.observe(this);
  }

  #stopAutoplayObserver() {
    this.#autoplayObserver?.disconnect();
    this.#autoplayObserver = undefined;
  }

  /**
   * ETOLIE 04.09.2026: wpis do nakładki diagnostycznej. Poza ?etolie-debug-wideo=1
   * nie robi nic.
   * @param {string} type
   * @param {Record<string, unknown>} [data]
   */
  #debug(type, data) {
    videoDebug?.log(this, type, data);
  }

  /**
   * ETOLIE 04.09.2026: jedyne miejsce zmiany intencji „ten slajd ma grać", żeby
   * nakładka diagnostyczna widziała każdą zmianę.
   * @param {boolean} value
   */
  #setWantsPlayback(value) {
    if (this.#wantsPlayback === value) return;
    this.#wantsPlayback = value;
    this.#released = !value;
    this.#debug('wantsPlayback', { val: value ? 1 : 0 });
  }

  /**
   * Keep the poster in the DOM for its opacity fade, but retire the already
   * consumed button from pointer input, keyboard navigation and the AT tree.
   */
  #retirePosterButton() {
    const posterButton = this.refs.deferredMediaPlayButton;
    if (!(posterButton instanceof HTMLElement)) return;

    posterButton.classList.add('deferred-media__playing');
    posterButton.setAttribute('aria-hidden', 'true');
    posterButton.setAttribute('tabindex', '-1');
    posterButton.setAttribute('inert', '');
    posterButton.style.pointerEvents = 'none';
  }

  /**
   * ETOLIE 04.09.2026: dokładna odwrotność #retirePosterButton. Używana tylko
   * wtedy, gdy przeglądarka odmówiła startu i plakat musi wrócić do klientki.
   */
  #restorePosterButton() {
    const posterButton = this.refs.deferredMediaPlayButton;
    if (!(posterButton instanceof HTMLElement)) return;

    posterButton.classList.remove('deferred-media__playing');
    posterButton.removeAttribute('aria-hidden');
    posterButton.removeAttribute('tabindex');
    posterButton.removeAttribute('inert');
    posterButton.style.removeProperty('pointer-events');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    reducedMotionQuery.removeEventListener('change', this.#handleReducedMotionChange);
    this.#abortController.abort();
    this.#stopAutoplayObserver();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    // ETOLIE 19.09.2026: klasa dla CSS, który gasi znak pauzy nad grającym filmem.
    this.classList.toggle('etolie-gra', isPlaying);
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Start an HTML video and keep the visual state aligned with its Promise.
   * AbortError is ignored only when one of our later pauseMedia() calls
   * invalidated this exact play attempt. Every other failure remains visible
   * as a rejected Promise after the control is returned to the paused state.
   * @param {HTMLVideoElement} video
   */
  #playVideo(video) {
    const playGeneration = this.#playGeneration;
    let playPromise;

    this.#debug('play()', { rs: video.readyState, ns: video.networkState });

    try {
      playPromise = video.play();
    } catch (error) {
      this.isPlaying = false;
      this.updatePlayPauseHint(false);
      this.#debug('play:throw', { name: /** @type {Error} */ (error)?.name });
      throw error;
    }

    this.isPlaying = true;
    this.updatePlayPauseHint(true);

    if (!playPromise || typeof playPromise.then !== 'function') return;

    // ETOLIE 04.09.2026: dokładnie jedno żądanie odtwarzania naraz. Poprzedni
    // układ potrafił wysłać trzy obietnice na ten sam element i nie dało się
    // odróżnić, która co zwróciła.
    this.#playPending = true;

    void playPromise.then(
      () => {
        this.#playPending = false;
        this.#debug('play:ok', { rs: video.readyState, paused: video.paused ? 1 : 0 });
      },
      (error) => {
        this.#playPending = false;
        this.#debug('play:err', { name: error?.name, rs: video.readyState, paused: video.paused ? 1 : 0 });

        const interruptedByOurPause = error?.name === 'AbortError' && playGeneration !== this.#playGeneration;
        if (interruptedByOurPause) return;

        if (playGeneration === this.#playGeneration) {
          this.isPlaying = false;
          this.updatePlayPauseHint(false);
        }

        // ETOLIE 04.09.2026: odmowa polityki (iOS, tryb niskiego zużycia energii)
        // nie jest awarią kodu — oddajemy klientce działający przycisk plakatu
        // zamiast martwej klatki. Warunek `paused` chroni przed odsłonięciem
        // plakatu nad filmem, który mimo spóźnionego odrzucenia już gra.
        if (error?.name === 'NotAllowedError' && video.paused) {
          this.#restorePosterButton();
          return;
        }

        // ETOLIE 04.09.2026: AbortError bez naszej pauzy znaczy, że obietnicę
        // skasował algorytm ładowania (HTML: „Take pending play promises and
        // reject pending play promises … AbortError"). Jedno ponowienie, gdy
        // film ma już dane; więcej nie próbujemy, żeby nie zapętlić startu.
        if (error?.name === 'AbortError' && !this.#autostartRetried && this.#wantsPlayback) {
          this.#autostartRetried = true;
          video.addEventListener('canplay', this.#tryAutostart, {
            once: true,
            signal: this.#abortController.signal,
          });
        }

        // Każdy inny powód zostaje w nakładce diagnostycznej — na produkcji nie
        // hałasujemy w konsoli klientki.
      }
    );
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus) {
      const toggleMediaButton = this.refs.toggleMediaButton;
      if (toggleMediaButton instanceof HTMLElement) {
        toggleMediaButton.classList.remove('hidden');
        toggleMediaButton.focus();
      } else if (content instanceof HTMLElement) {
        content.focus();
      }
    }

    if (content instanceof HTMLVideoElement) {
      // ETOLIE 04.09.2026: żadnego play() w tym samym zadaniu, w którym element
      // trafia do DOM (readyState 0, networkState 3 — algorytm wyboru zasobu
      // jeszcze nie ruszył i przerywa obietnicę). Klon niesie atrybut `autoplay`,
      // więc start należy do przeglądarki; JS tylko asekuruje w #tryAutostart.
      this.#hasPlayed = false;
      this.#loadStarted = false;
      this.#autostartWaited = false;
      this.#autostartDeferred = false;
      this.#autostartRetried = false;
      this.#playPending = false;
      this.#debug('insert', { rs: content.readyState, ns: content.networkState });
      this.#armAutostart(content);

      if (focus) {
        // Klik w plakat: start MUSI zostać w tym samym geście, bo iOS w trybie
        // niskiego zużycia energii przepuszcza wyłącznie odtwarzanie z gestu.
        // ETOLIE 04.09.2026: klientka poprosiła wprost, więc od tej chwili ten
        // element ma grać — inaczej #handleVideoPlaying uznałby start za
        // spóźniony i od razu go zatrzymał.
        this.#setWantsPlayback(true);
        this.#retirePosterButton();
        this.#playVideo(content);
      }
      return;
    }

    // iframe (YouTube/Vimeo) i model nie wysyłają `playing`, więc dla nich
    // zostaje stary kontrakt: wstawienie treści = start odtwarzania.
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
    this.#retirePosterButton();
  }

  /**
   * ETOLIE 04.09.2026: wstawia odtwarzacz z wyprzedzeniem, bez deklaracji, że ma
   * grać. Woła to galeria dla slajdu bieżącego i sąsiednich.
   */
  prepareMedia() {
    if (!this.autoplayAllowed()) return;
    this.#debug('prepareMedia');
    this.loadContent(false);
  }

  /**
   * ETOLIE 04.09.2026: jedyne wejscie "ten slajd jest teraz biezacy i ma grac".
   * Galeria woła je po zatrzymaniu karuzeli i po zamknięciu powiększenia.
   * Idempotentne.
   */
  ensureAutoplay() {
    if (!this.autoplayAllowed()) return;
    this.#debug('ensureAutoplay');
    this.#setWantsPlayback(true);
    this.loadContent(false);
    this.#tryAutostart();
  }

  /**
   * ETOLIE 04.09.2026: odwrotność ensureAutoplay — ten slajd przestał być
   * bieżący. Potrzebna, bo obserwator widoczności nie widzi zejścia z kadru:
   * sąsiedni slajd karuzeli zgłasza `isIntersecting: true` przy ratio ~0,44,
   * więc warunek pauzy nigdy się nie spełnia. Dotąd film zatrzymywała sama
   * przeglądarka (autoplay poza kadrem), ale jawne play() zdejmuje w WebKicie
   * restrykcję `InvisibleAutoplayNotPermitted` i ta pomoc znika.
   */
  releasePlayback() {
    this.#setWantsPlayback(false);
    if (this.hasAttribute('data-media-loaded')) this.pauseMedia();
  }

  /**
   * ETOLIE 04.09.2026: nasłuchy na samym `<video>`. `loadstart` mówi, że ruszył
   * algorytm ładowania — dopiero po nim play() nie zostanie skasowane. Progi
   * gotowości danych zostają jako druga szansa; play() na grającym filmie jest
   * wtedy pustą operacją.
   * @param {HTMLVideoElement} video
   */
  #armAutostart(video) {
    if (this.#armedVideo === video) return;
    this.#armedVideo = video;

    const { signal } = this.#abortController;
    video.addEventListener('playing', this.#handleVideoPlaying, { signal });
    video.addEventListener('pause', this.#handleVideoPause, { signal });
    video.addEventListener('loadstart', this.#handleLoadStart, { signal });
    video.addEventListener('loadeddata', this.#tryAutostart, { signal });
    video.addEventListener('canplay', this.#tryAutostart, { signal });

    if (videoDebug) {
      for (const type of debuggedVideoEvents) {
        video.addEventListener(
          type,
          () =>
            this.#debug(type, {
              rs: video.readyState,
              ns: video.networkState,
              paused: video.paused ? 1 : 0,
              code: video.error ? video.error.code : null,
            }),
          { signal }
        );
      }
    }

    this.#tryAutostart();
  }

  /** ETOLIE 04.09.2026: algorytm ładowania ruszył, więc play() jest już bezpieczne. */
  #handleLoadStart = () => {
    this.#loadStarted = true;
    this.#tryAutostart();
  };

  /**
   * ETOLIE 04.09.2026: JEDYNE miejsce, które woła play() bez udziału klientki.
   * Nie patrzymy na `readyState`: na iOS to właśnie play() zmusza WebKit do
   * pobrania danych (HTMLMediaElement::playInternal uruchamia wybór zasobu, gdy
   * „loading was suppressed (e.g. iOS autoplay policy)"), a ścieżka atrybutu
   * `autoplay` wymaga HAVE_ENOUGH_DATA, którego przy preload="metadata" nigdy
   * nie ma. Czekanie na dane przed play() było zakleszczeniem: my czekaliśmy na
   * telefon, telefon na nas. Gdy przeglądarka wystartowała film sama, `paused`
   * jest już false i nie odbieramy jej tego mechanizmu.
   */
  #tryAutostart = () => {
    if (!this.#wantsPlayback || !this.autoplayAllowed()) return;
    if (this.#playPending) return;

    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;
    if (!video.paused) return;

    // Jedyny warunek: nie wołamy play() w tym samym zadaniu, w którym element
    // trafił do DOM. Algorytm ładowania kasuje wtedy zaległą obietnicę
    // AbortError-em („The play() request was interrupted by a new load request").
    if (video.readyState === HTMLMediaElement.HAVE_NOTHING && !this.#loadStarted && !this.#autostartWaited) {
      this.#deferAutostart();
      return;
    }

    this.#playVideo(video);
  };

  /**
   * ETOLIE 04.09.2026: jedno odroczenie o zadanie. Bierzemy to, co przyjdzie
   * pierwsze — `loadstart` albo koniec bieżącego zadania. Ten drugi wariant
   * musi być, bo na iOS wybór zasobu bywa wstrzymany aż do pierwszego play()
   * i wtedy `loadstart` nie przyjdzie nigdy.
   */
  #deferAutostart() {
    if (this.#autostartDeferred) return;
    this.#autostartDeferred = true;
    setTimeout(() => {
      this.#autostartDeferred = false;
      this.#autostartWaited = true;
      this.#tryAutostart();
    }, 0);
  }

  /** ETOLIE 04.09.2026: plakat gaśnie dopiero po realnym starcie filmu. */
  #handleVideoPlaying = () => {
    this.#hasPlayed = true;

    // ETOLIE 04.09.2026: na wolnym łączu film potrafi ruszyć już po tym, jak
    // klientka zjechała ze slajdu. Teraz pauza jest bezpieczna, bo film
    // naprawdę ruszył — to `#hasPlayed` zdejmuje blokadę w pauseMedia().
    // Warunek to `#released`, nie sam brak intencji: start z ręki przeglądarki
    // na dojeżdżającym slajdzie jest w porządku i nie ma po co go przerywać.
    if (this.#released) {
      this.pauseMedia();
      return;
    }

    this.isPlaying = true;
    this.updatePlayPauseHint(true);
    this.#retirePosterButton();
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
  };

  #handleVideoPause = () => {
    this.isPlaying = false;
    this.updatePlayPauseHint(false);
  };

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    // ETOLIE 04.09.2026: jawna prośba o odtwarzanie (przycisk play) też ustawia
    // intencję, inaczej #handleVideoPlaying zatrzymałby film zaraz po starcie.
    this.#setWantsPlayback(true);

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        this.#playVideo(video);
        return;
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    const video = iframe ? null : this.querySelector('video');

    // ETOLIE 04.09.2026: nie pauzujemy filmu, który ani razu nie ruszył. WebKit
    // trzyma świeżo wstawione `<video autoplay>` w stanie przerwanym, dopóki
    // slajd nie jest widoczny; pause() w tym oknie niczego nie zatrzymuje, ale
    // zapisuje "ma byc zatrzymany" i film nie rusza już nigdy. Generację
    // podbijamy tylko przy realnej pauzie, żeby filtr AbortError nie brał
    // prawdziwej awarii za nasz wyścig play/pause.
    const canPause = iframe != null || (video instanceof HTMLVideoElement && this.#hasPlayed && !video.paused);

    if (canPause) {
      this.#debug('pause()', { hasPlayed: this.#hasPlayed ? 1 : 0 });
      this.#playGeneration += 1;

      if (iframe) {
        iframe.contentWindow?.postMessage(
          iframe.dataset.videoType === 'youtube'
            ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
            : '{"method":"pause"}',
          '*'
        );
      } else {
        video?.pause();
      }
    }

    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

// ETOLIE 04.09.2026: dokumentowy nasluch `slideshow:select` z 03.08.2026 zostal
// usuniety. Byl druga i trzecia sciezka startu na to samo <video> oraz globalna
// petla pauzowania. Obowiazki przejmuje assets/media-gallery.js, zakresowane do
// wlasnej galerii i wlasnej karuzeli; prezentacja `grid` zostaje przy samym
// obserwatorze widocznosci.

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
